/**
 * Admin Portal — SME Management Page
 * Manage SME lifecycle, domain assignments, and availability.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControl, InputLabel, Select, MenuItem, Alert, Switch,
  FormControlLabel, Stack,
} from '@mui/material';
import { Edit, CheckCircle, Cancel } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

interface SMERecord {
  id: string;
  name: string;
  email: string;
  role: string;
  domains: string[];
  is_active: boolean;
  is_online: boolean;
  last_login: string | null;
}

const DOMAINS = [
  // Security & Compliance
  'Cloud Security Adoption',
  'Zero Trust Maturity',
  'Data Security Copilot',
  // Cloud Strategy & Adoption
  'Cloud Maturity',
  'Azure Landing Zone',
  'Azure VMware Solution',
  'Well-Architected Framework',
  'Cloud Adoption Framework',
  // AI & Analytics
  'AI Readiness',
  'AI Engineer Skills',
  'Agentic AI Readiness',
  'Fabric Analytics Journey',
  'GenAI Maturity',
  // Development & Operations
  'DevOps Capability',
  'App Data Modernization',
  'Azure Integration Service',
  'DevSecOps Capability',
  // Productivity & Collaboration
  'M365 Copilot Readiness',
  'Copilot Studio Readiness',
  'Power Platform Adoption',
  'Power Platform Well-Architected',
  'Dynamics 365 F&O Readiness',
  'Dynamics 365 Sales & Field Service',
  'Dynamics 365 Contact Center',
  // Baseline Architecture
  'Baseline Architecture',
];

const SMEManagement: React.FC = () => {
  const [smes, setSmes] = useState<SMERecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [editSme, setEditSme] = useState<SMERecord | null>(null);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState('');

  const fetchSMEs = useCallback(() => {

    setLoading(true);
    authFetch('/api/admin/portal/smes')
      .then((r) => r.json())
      .then((data) => { setSmes(data.smes || []); setLastRefreshed(new Date()); setLoading(false); })
      .catch(() => { setLastRefreshed(new Date()); setLoading(false); });

  }, []);

  useEffect(() => { fetchSMEs(); }, [fetchSMEs]);

  const handleSave = async () => {
    if (!editSme) return;
    try {
      const res = await authFetch(`/api/admin/portal/smes/${editSme.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domains: selectedDomains, is_active: isActive }),
      });
      if (!res.ok) throw new Error('Failed to update SME');
      setSmes((prev) =>
        prev.map((s) => s.id === editSme.id ? { ...s, domains: selectedDomains, is_active: isActive } : s)
      );
      setEditSme(null);
    } catch {
      setError('Failed to update SME. Check permissions.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          SME Management
        </Typography>
        <RefreshButton onRefresh={fetchSMEs} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage Subject Matter Expert assignments, domains, and availability.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Domains</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6}>Loading...</TableCell></TableRow>
            ) : smes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No SMEs found. Assign SME roles to users in Role Management to see them here.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              smes.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{s.email}</TableCell>
                  <TableCell>
                    <Chip label={s.role.replace(/_/g, ' ')} size="small" color="success" />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(s.domains || []).map((d) => (
                        <Chip key={d} label={d} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {s.is_online ? (
                      <Chip icon={<CheckCircle />} label="Online" size="small" color="success" />
                    ) : s.is_active ? (
                      <Chip icon={<Cancel />} label="Offline" size="small" color="default" />
                    ) : (
                      <Chip icon={<Cancel />} label="Deactivated" size="small" color="error" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => {
                      setEditSme(s);
                      setSelectedDomains(s.domains || []);
                      setIsActive(s.is_active);
                    }}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Edit SME Dialog */}
      <Dialog open={!!editSme} onClose={() => setEditSme(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit SME — {editSme?.name}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Domains</InputLabel>
            <Select
              multiple
              value={selectedDomains}
              label="Domains"
              onChange={(e) => setSelectedDomains(e.target.value as string[])}
              renderValue={(sel) => (sel as string[]).join(', ')}
            >
              {DOMAINS.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label="Active"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSme(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SMEManagement;
